<script lang="ts">
  import type {
    MediaKitMoreMediaSection,
    ShapeLinkGroup as ShapeLinkGroupSchema
  } from '$lib/sanity/schemas';
  import { referencesStore } from '$lib/stores/references';
  import { BGMode } from '$components/News/Background/BackgroundGradient.svelte';
  import LinkGroup from '$components/SanityComponents/Common/ShapeLinkGroup.svelte';
  import { ResponsiveVars } from '@twist/core';
  import { cx } from '@emotion/css';
  import {
    useEncodeDataAttribute,
    type ContentSourceMap,
    type StudioPathLike
  } from '@sanity/svelte-loader';
  import { sanityStudioURL } from '$lib/client';

  export let document: MediaKitMoreMediaSection;
  export let sourceMap: ContentSourceMap | undefined = undefined;
  export let path: StudioPathLike = [];

  $: links = document.links && referencesStore.dereference<ShapeLinkGroupSchema>(document.links);
  $: encodeDataAttribute = useEncodeDataAttribute(document, sourceMap, sanityStudioURL);

  const responsiveVars = new ResponsiveVars()
    .addFluid('container-padding', [
      ['min', 64],
      ['lg', 'max', 64, 128],
      ['max', 128]
    ])
    .addFluid('header-font-size', [
      ['min', 48],
      ['sm', 'lg', 48, 52],
      ['lg', 'max', 52, 72],
      ['max', 72]
    ])
    .addFluid('caption-font-size', [
      ['min', 14],
      ['sm', 'lg', 14, 16],
      ['lg', 'max', 16, 32],
      ['max', 32]
    ])
    .addFluid('caption-margin-top', [
      ['min', 24],
      ['lg', 'max', 24, 32],
      ['max', 32]
    ])
    .addFluid('caption-margin-bottom', [
      ['min', 48],
      ['lg', 'max', 48, 96],
      ['max', 96]
    ])
    .toCSS();
</script>

<div
  class={cx(responsiveVars, 'moreMediaSectionContainer')}
  data-background
  data-bgmode={BGMode.SINGLE}
  data-bgcolor1={document.bgColor}
>
  <h2 class="header" data-sanity={encodeDataAttribute([...path, 'title'])}>{document.title}</h2>

  <p class="caption" data-sanity={encodeDataAttribute([...path, 'caption'])}>{document.caption}</p>

  {#if links}
    <div class="links">
      <LinkGroup {sourceMap} {path} document={links} />
    </div>
  {/if}
</div>

<style lang="scss">
  @import 'src/scss/media';

  .moreMediaSectionContainer {
    padding: var(--container-padding) 0;
  }

  .header {
    color: var(--twist-color-gray-100);
    font-family: var(--font-family-primary);
    font-size: var(--header-font-size);
    font-weight: 700;
  }

  .caption {
    color: var(--twist-color-gray-100);
    font-family: var(--font-family-primary);
    font-size: var(--caption-font-size);
    font-weight: 400;
    line-height: 150%;
    margin-top: var(--caption-margin-top);
    margin-bottom: var(--caption-margin-bottom);
  }

  .links {
    @include at('lg') {
      max-width: 66%;
    }
  }
</style>
